// The core "What is Cortex?" tour — the full product walkthrough every project
// gets. Explains the system part by part, navigating to each surface and
// spotlighting it. Declarative: see TutorialFlow.jsx for the step schema.

export const coreTour = {
  key: "core",
  title: "What is Cortex?",
  description:
    "The full product tour: projects & branches, the data dictionary (elements, sources, mappings, reference lists, processing rules), quality, the pipeline, apps and the assistant.",
  steps: [
    {
      title: "Welcome to TIVA Cortex",
      body:
        "Cortex is a <b>data-governance studio</b>. You describe your data as a <b>recipe</b> — " +
        "the canonical fields you care about, where the raw data lives, how it must be cleaned and " +
        "validated, and where the governed result should land. A paired <b>engine</b> then executes " +
        "that recipe as a pipeline.<br/><br/>Every piece of the recipe is <b>versioned like code</b>: " +
        "each change is a commit in the project's own branchable database, so you always know what " +
        "changed, when, and can stage work on branches.<br/><br/>This tour walks through every part " +
        "of the system. Use <b>Next / Back</b> or the arrow keys; <b>Esc</b> exits.",
    },
    {
      target: '[data-tour="project-branch-chip"]',
      title: "Projects & branches",
      body:
        "Everything you'll see belongs to the <b>active project</b> — one project = one recipe = one " +
        "versioned database, paired with its own engine workspace. The chip shows the project and the " +
        "<b>branch</b> you are editing. Branches work like git: experiment on a branch, diff it, merge " +
        "it back. The URL always carries both, so links are shareable and unambiguous.",
      placement: "right",
    },
    {
      page: "dashboard",
      target: '[data-tour="page-dashboard"], main',
      title: "Dashboard — the project at a glance",
      body:
        "The health overview: how many elements are defined, which sources are connected and mapped, " +
        "data-quality signals and recent pipeline activity. When something needs attention, this is " +
        "where it surfaces first.",
    },
    {
      target: '[data-tour="nav-group-data-governance"]',
      title: "Data Governance — the data dictionary",
      body:
        "The heart of Cortex. This group holds the <b>recipe</b>: Elements, Sources, Source Mappings, " +
        "Reference Lists, Processing Rules, Data Review and Quality. The next steps visit each one in " +
        "the order you'd naturally build a project.",
      placement: "right",
    },
    {
      page: "data-elements",
      target: '[data-tour="page-data-elements"], main',
      title: "Data Elements — your canonical fields",
      body:
        "An <b>element</b> is one governed business field: its name, data type, category, whether it's " +
        "a primary key. Elements are the vocabulary everything else speaks: source columns are mapped " +
        "<i>into</i> elements, processing rules are attached <i>to</i> elements, and targets receive " +
        "columns bound <i>from</i> elements. Define these first — or let the assistant derive them " +
        "from a file you upload.",
    },
    {
      page: "data-sources",
      target: '[data-tour="page-data-sources"], main',
      title: "Sources — where raw data comes from",
      body:
        "A <b>source</b> is a connection to real data: an uploaded file (CSV/XLSX), a Postgres or " +
        "MongoDB database, an API… Connecting a source captures its <b>schema</b>, and analysis " +
        "profiles every column (types, fill rates, samples) so you can see what you're dealing with " +
        "before any mapping happens.",
    },
    {
      demo: "new-source",
      target: '[data-testid="source-type-trigger"], [data-tutorial-demo-modal]',
      title: "Creating a source — the real form",
      body:
        "This is the actual create form, opened with sample values (the tour never saves " +
        "anything). Give the source a <b>name</b>, pick its <b>Type</b> — file upload, database " +
        "connector, or an ✨ AI-built source described in plain language — and the form shows " +
        "exactly the fields that type needs. File sources are created first and get their file " +
        "uploaded on the source's own page.",
    },
    {
      page: "source-mappings",
      target: '[data-tour="page-source-mappings"], main',
      title: "Source Mappings — wiring columns to elements",
      body:
        "A <b>mapping</b> says “this source column feeds that data element”. It's the contract that " +
        "makes a source usable in a pipeline — unmapped columns simply don't flow. Mappings can be " +
        "created by hand, or the assistant can <b>suggest them with an LLM</b> from the analyzed " +
        "schema and element names.",
    },
    {
      page: "reference-lists",
      target: '[data-tour="page-reference-lists"], main',
      title: "Reference Lists — controlled vocabularies",
      body:
        "A <b>reference list</b> is the set of allowed values for a field (countries, statuses, " +
        "species…). Validation rules check incoming values against these lists and can standardize " +
        "variants to the canonical value — so “USA”, “U.S.” and “United States” land as one thing.",
    },
    {
      page: "processing-rules",
      target: '[data-tour="page-processing-rules"], main',
      title: "Processing Rules — clean & validate every record",
      body:
        "A <b>rule</b> is a small transformation or validation applied to an element as data flows " +
        "through the pipeline: trim whitespace, normalize numbers and currencies, parse dates, " +
        "validate against a reference list… Rules are attached to elements and run on <b>every " +
        "record</b>; they scale to hundreds of columns without any custom code per column.",
    },
    {
      page: "data-review",
      target: '[data-tour="page-data-review"], main',
      title: "Data Review — inspect what came through",
      body:
        "The workbench for looking at processed records: browse them, spot problems the rules " +
        "flagged, and fix the recipe (a mapping, a rule, a reference value) rather than patching " +
        "data by hand.",
    },
    {
      page: "dictionary-quality",
      target: '[data-tour="page-dictionary-quality"], main',
      title: "Quality — completeness & validity metrics",
      body:
        "Per element and per source: how complete the data is, how much passes validation, where the " +
        "gaps are. Quality is measured against the <b>recipe you declared</b> — which is what makes " +
        "it governance rather than guesswork.",
    },
    {
      page: "pipeline",
      target: '[data-tour="page-pipeline"], main',
      title: "Pipeline — the executable graph",
      body:
        "The recipe compiles into a <b>graph of nodes</b>: <b>load</b> nodes (your sources), " +
        "<b>transform</b> nodes (typed transformations — e.g. applying the processing rules), and " +
        "<b>save</b> nodes (your targets), connected by links. Press <b>Run</b> and the engine " +
        "executes the graph: reads the source, applies mappings and rules, writes the governed rows " +
        "to the target. Runs appear below with their status and row counts.",
    },
    {
      page: "apps",
      target: '[data-tour="apps-rail"], main',
      title: "Apps — dashboards on governed data",
      body:
        "Each project can publish <b>apps</b> — dashboards and tools built on the engine that read " +
        "the <b>governed target live</b>. Because they sit downstream of the pipeline, what you see " +
        "is always the validated, rule-processed data — not a copy pasted into a chart.",
    },
    {
      target: '[data-tour="nav-flow"]',
      title: "Flow — the engine studio",
      body:
        "For power users: the embedded <b>Flow studio</b> exposes the engine workspace paired with " +
        "this project — the services and endpoints that actually execute pipelines and serve apps. " +
        "Day-to-day governance never requires touching it, but everything is inspectable.",
      placement: "right",
    },
    {
      target: '[data-tour="nav-branches"]',
      title: "Branches — version control for the recipe",
      body:
        "Every change you make — an element, a mapping, a rule — is a <b>commit</b>. Create a branch " +
        "to stage bigger changes, compare it against main with a diff, and merge when ready. The " +
        "audit trail is built in; nothing is ever silently overwritten.",
      placement: "right",
    },
    {
      target: ['[data-tour="chat-launcher-orchestrator"]'],
      title: "The assistant — build it by chatting",
      body:
        "Everything this tour showed can be done by <b>asking the assistant</b>: “add this file as a " +
        "source”, “map it to my elements”, “create a rule that normalizes currencies”, “run the " +
        "pipeline”, “chart sales by region”. It routes your request to the right specialist " +
        "(governance, engine, analyst) and uses the same typed, versioned operations you'd use in " +
        "the UI.",
      placement: "left",
    },
    {
      title: "That's Cortex",
      body:
        "<b>Recipe</b> (elements → sources → mappings → reference lists → rules) → <b>pipeline</b> " +
        "(load → transform → save) → <b>governed target</b> → <b>apps & analysis</b>, all versioned " +
        "on branches, all drivable by chat.<br/><br/>You can replay this tour any time from " +
        "<b>Tutorial</b> in the sidebar. Projects can ship their own extra tours — when the active " +
        "project has one, the Tutorial button offers it.",
    },
  ],
};
